import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PageRibbon, ScoreBadge, TimeWindow } from '../../components';
import { useAuth } from '../../context';
import { apiService } from '../../services';
import type { LeaderboardData, LeaderboardUser } from '../../types';
import { defaultRangeDays, type RangePresetDays } from '../../utils/formatKpi';
import '../Dashboard/Dashboard.css';
import './Leaderboard.css';

const TrophyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 9H4.5a2.5 2.5 0 010-5H6M18 9h1.5a2.5 2.5 0 000-5H18" />
    <path d="M4 22h16" />
    <path d="M18 2H6v7a6 6 0 0012 0V2z" />
  </svg>
);

function formatMetric(n: number | null, digits = 1): string {
  if (n == null) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

type LeaderboardSortKey = 'overall' | 'tc' | 'pc' | 'attach';

function sortValue(user: LeaderboardUser, key: LeaderboardSortKey): number {
  switch (key) {
    case 'tc':
      return user.avg_task_clarity ?? -1;
    case 'pc':
      return user.avg_prompt_context ?? -1;
    case 'attach':
      return user.avg_attach_rate ?? -1;
    default:
      return user.avg_overall_score ?? -1;
  }
}

function sortUsersDesc(users: LeaderboardUser[], key: LeaderboardSortKey): LeaderboardUser[] {
  return [...users].sort((a, b) => {
    const diff = sortValue(b, key) - sortValue(a, key);
    if (diff !== 0) return diff;
    return (a.display_name || a.email).localeCompare(b.display_name || b.email);
  });
}

function Row({ user, isMe }: { user: LeaderboardUser; isMe: boolean }) {
  return (
    <tr className={isMe ? 'me' : undefined}>
      <td className="pa-table__num num">{user.rank ?? '—'}</td>
      <td>
        {user.display_name}
        {isMe && <span className="pill" style={{ marginLeft: 8 }}>You</span>}
      </td>
      <td className="pa-table__num num">{user.prompt_count}</td>
      <td className="pa-table__num num">{formatMetric(user.avg_task_clarity)}</td>
      <td className="pa-table__num num">{formatMetric(user.avg_prompt_context)}</td>
      <td className="pa-table__num"><ScoreBadge score={user.avg_overall_score} /></td>
      <td className="pa-table__num num">{formatMetric(user.avg_attach_rate)}</td>
    </tr>
  );
}

export const Leaderboard: React.FC = () => {
  const { user } = useAuth();
  const [days, setDays] = useState<RangePresetDays>(7);
  const range = defaultRangeDays(days);
  const [data, setData] = useState<LeaderboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<LeaderboardSortKey>('overall');

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getLeaderboard(range.start, range.end);
      if (response.success) setData(response.data);
      else setError(response.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  }, [range.start, range.end]);

  useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);

  const sortedTop = useMemo(
    () => (data ? sortUsersDesc(data.top_users, sortKey) : []),
    [data, sortKey]
  );

  const setSort = (key: LeaderboardSortKey) => {
    setSortKey(key);
  };

  const sortHeader = (key: LeaderboardSortKey, label: string, hint: string) => (
    <button
      type="button"
      className={`pa-table__sort-btn${sortKey === key ? ' pa-table__sort-btn--active' : ''}`}
      onClick={() => setSort(key)}
      aria-pressed={sortKey === key}
    >
      {label}
      <small>{hint}</small>
    </button>
  );

  return (
    <div className="dashboard">
      <main className="dashboard__content">
        <PageRibbon
          title="Leaderboard"
          icon={<TrophyIcon />}
          subtitle={`Top 20 for the last ${days} days. Sorted by highest ${sortKey === 'overall' ? 'average overall score' : sortKey === 'tc' ? 'task clarity' : sortKey === 'pc' ? 'prompt context' : 'attach rate'} first.`}
          actions={<TimeWindow value={days} onChange={setDays} />}
        />
        {loading && <div className="leaderboard-page__skeleton" />}
        {error && (
          <div className="leaderboard-page__error">
            <p>{error}</p>
            <button type="button" onClick={fetchLeaderboard}>Retry</button>
          </div>
        )}
        {data && !loading && (
          <>
            <div className="pa-card">
              <div className="pa-table-wrap">
                <table className="pa-table">
                  <thead>
                    <tr>
                      <th className="pa-table__num">Rank</th>
                      <th>Name</th>
                      <th className="pa-table__num">Prompts</th>
                      <th className="pa-table__num">
                        {sortHeader('tc', 'Avg task clarity', 'out of 10')}
                      </th>
                      <th className="pa-table__num">
                        {sortHeader('pc', 'Avg prompt context', 'out of 10')}
                      </th>
                      <th className="pa-table__num">
                        {sortHeader('overall', 'Avg overall score', 'out of 10')}
                      </th>
                      <th className="pa-table__num">
                        {sortHeader('attach', 'Avg attach rate', 'skill files per prompt')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTop.map((entry) => (
                      <Row key={entry.email} user={entry} isMe={entry.email === user?.email} />
                    ))}
                    {data.include_current_user && (
                      <Row user={data.current_user} isMe />
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="dashboard__footnote">
              Names are not links, and no prompt opens from this page. Attach rate is the average number of skill files attached per prompt.
            </p>
          </>
        )}
      </main>
    </div>
  );
};

export default Leaderboard;
